import { normalize, schema } from 'normalizr';
import { NormalizeRequest } from './normalize.request';
import { NormalizeResponse } from './normalize-response';
import { isArray, isNullOrUndefined } from '@core/utils/object';
import { isString } from '@core/utils/string';
import { isSerializable, isDeprecatedSerialisable, isForceConstructor } from '@core/utils/serializable';
import { PageMeta } from '@core/models/common/page-meta';
import { map } from 'rxjs';

/**
 * Normalize operator.
 * Normalizes an array of entities or supports multiple entities via the first param.
 * @param {string | NormalizeRequest[]} entityName
 * @param entityClass
 * @param {string} key
 * @returns {any}
 */
export const ossNormalize = (
    entityName: string | schema.Entity | NormalizeRequest[],
    entityClass?: any,
    key?: string,
    ignoredIds?: string[]
): any => {
    const emptyResponse = {
        result: [],
        entities: {},
        items: [],
        data: null,
    };

    const processIgnoredIds = (dataList: any[], ids?: string[]): any => {
        if (ids && ids.length) {
            return dataList.filter((item) => ids.indexOf(item.id) === -1);
        } else {
            return dataList;
        }
    };

    /**
     * Normalize process for an entity.
     * @param pValue
     * @param {string} pEntityName
     * @param pEntityClass
     * @param {string} pKey
     * @returns {any}
     */
    const processFn: any = (
        pValue: any,
        pEntityName: string | schema.Entity,
        pEntityClass?: any,
        pKey?: string,
        ignoredIds?: string[]
    ): NormalizeResponse => {
        if (!pValue.hasOwnProperty(pKey) || isNullOrUndefined(pValue[pKey])) {
            return { ...emptyResponse, data: pValue };
        }

        // Normalize
        let dataSchema: any;
        if (isString(pEntityName)) {
            dataSchema = [new schema.Entity(pEntityName as string)];
        } else {
            dataSchema = [pEntityName as schema.Entity];
        }

        const processedData: any = processIgnoredIds(pValue[pKey], ignoredIds);
        const normalizedData: {
            entities: any;
            result: any;
        } = normalize(processedData, dataSchema);

        // Cast as domain objects
        if (pEntityClass) {
            const domainEntities: any =
                normalizedData.entities[dataSchema[0].key];
            if (domainEntities) {
                Object.keys(domainEntities).forEach((key) => {
                    if (isSerializable(pEntityClass)) {
                        domainEntities[key] = pEntityClass.import(
                            domainEntities[key]
                        );
                    } else if (isDeprecatedSerialisable(pEntityClass)) {
                        // NOTE:A: Remove after full serialization refactor
                        domainEntities[key] = pEntityClass.fromJson(
                            domainEntities[key]
                        );
                    } else if (isForceConstructor(pEntityClass)) {
                        domainEntities[key] = new pEntityClass(
                            domainEntities[key]
                        );
                    } else {
                        domainEntities[key] = Object.assign(
                            new pEntityClass(),
                            domainEntities[key]
                        );
                    }
                });
            }
        }

        const normalized: NormalizeResponse = {
            result: normalizedData.result,
            entities: normalizedData.entities,
            items: processedData,
            data: pValue,
        };

        // Support paged and non-paged results
        if (pValue.hasOwnProperty('meta')) {
            normalized.meta = Object.assign(new PageMeta(), pValue['meta']);
        }

        return normalized;
    };

    return map((value) => {
        if (isNullOrUndefined(value)) {
            return { ...emptyResponse };
        }

        if (isArray(entityName)) {
            // Normalize multiple entities
            return (entityName as any[]).reduce(
                (accumulator: any, currentValue: any, currentIndex: any) => {
                    const { key, entityName, entityClass } = currentValue;
                    accumulator[currentValue.key] = processFn(
                        value,
                        entityName,
                        entityClass,
                        key,
                        ignoredIds
                    );

                    return accumulator;
                },
                { data: value }
            );
        } else {
            // Normalize single entity
            return processFn(value, entityName, entityClass, key, ignoredIds);
        }
    });
};
